// Fair Price Intelligence Dashboard.
//
// Punch-list task #2 — surface every fixture the engine has scored.
// Bound to useFairPriceOutputs (fair_price_outputs ⨝ canonical_fixtures
// ⨝ teams ⨝ competitions ↩ clv_consensus). NOT bound to signal_cards;
// that path was the bug — only ~2 fixtures had been published while
// the agents had produced 35 upcoming.
//
// Markup follows the prototype at UI Refactor/Build/fair-price-dashboard.jsx
// verbatim: same .ex-stats strip, same .fp-dash-grid layout, same
// .fp-side-card sidebar with league calibration + pipeline copy.
// Mockdata from the prototype (FP_LEAGUE_PERF stub list, hardcoded
// stat numbers, fictional team strings) is NOT introduced.

import { useMemo, useState } from "react";
import { Icon } from "../../components/icons/Icon.tsx";
import {
  useFairPriceOutputs,
  type FairPriceFixture,
  type FairPriceRow,
} from "../../exchange/useFairPriceOutputs.ts";
import { FairPriceDrawer } from "./FairPriceDrawer.tsx";
import { FairPriceList } from "./FairPriceList.tsx";

interface DashStat {
  k: string;
  v: string;
  unit: string;
  delta: string | null;
  deltaCls: "" | "pos" | "neg";
}

interface LeagueCalibrationRow {
  competition: string;
  picks: number;
  avg_edge: number | null;
}

function fmtSigned(p: number | null, digits: number = 1): string {
  if (p === null || !Number.isFinite(p)) return "—";
  const sign = p > 0 ? "+" : "";
  return `${sign}${p.toFixed(digits)}%`;
}

function fmtClock(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function deriveStats(rows: readonly FairPriceRow[], fixtures: readonly FairPriceFixture[]): DashStat[] {
  const total = fixtures.length;
  const headlines = fixtures.map((f) => f.headline_row);
  const withEdge = headlines.map((r) => r.edge_pct).filter((e): e is number => e !== null);
  const avgEdge =
    withEdge.length > 0 ? withEdge.reduce((a, b) => a + b, 0) / withEdge.length : null;
  const sharpest =
    withEdge.length > 0 ? withEdge.reduce((m, v) => (v > m ? v : m), -Infinity) : null;
  const positives = withEdge.filter((e) => e >= 5).length;
  const distinctMarkets = new Set(rows.map((r) => r.market)).size;
  const lastComputed =
    rows.length > 0
      ? rows.reduce((m, r) => (r.computed_at > m ? r.computed_at : m), rows[0]!.computed_at)
      : null;

  return [
    {
      k: "Markets scored",
      v: String(total),
      unit: "fixtures",
      delta:
        positives > 0 ? `${positives} with edge ≥ +5%` : "no fixture above +5% edge",
      deltaCls: "",
    },
    {
      k: "Avg edge (live)",
      v: avgEdge !== null ? avgEdge.toFixed(1) : "—",
      unit: "%",
      delta: avgEdge !== null ? "vs bookmaker consensus" : "no consensus yet",
      deltaCls: avgEdge !== null && avgEdge > 0 ? "pos" : avgEdge !== null && avgEdge < 0 ? "neg" : "",
    },
    {
      k: "Sharpest edge",
      v: sharpest !== null ? `${sharpest > 0 ? "+" : ""}${sharpest.toFixed(1)}` : "—",
      unit: "%",
      delta: sharpest !== null ? "single best market" : "—",
      deltaCls: sharpest !== null && sharpest > 0 ? "pos" : "",
    },
    {
      k: "Distinct markets",
      v: String(distinctMarkets),
      unit: "types",
      delta: `${rows.length} rows total`,
      deltaCls: "",
    },
    {
      k: "Last computed",
      v: lastComputed !== null ? fmtClock(lastComputed) : "—",
      unit: "",
      delta: "engine timestamp",
      deltaCls: "",
    },
  ];
}

function deriveLeagueCalibration(
  fixtures: readonly FairPriceFixture[],
): readonly LeagueCalibrationRow[] {
  const byLeague = new Map<string, { picks: number; edges: number[] }>();
  for (const f of fixtures) {
    const k = f.competition;
    const entry = byLeague.get(k) ?? { picks: 0, edges: [] };
    entry.picks += 1;
    const e = f.headline_row.edge_pct;
    if (e !== null) entry.edges.push(e);
    byLeague.set(k, entry);
  }
  const out: LeagueCalibrationRow[] = [];
  for (const [competition, e] of byLeague) {
    const avg = e.edges.length > 0 ? e.edges.reduce((a, b) => a + b, 0) / e.edges.length : null;
    out.push({ competition, picks: e.picks, avg_edge: avg });
  }
  return out.sort((a, b) => {
    if (a.avg_edge !== null && b.avg_edge !== null) return b.avg_edge - a.avg_edge;
    if (a.avg_edge !== null) return -1;
    if (b.avg_edge !== null) return 1;
    return b.picks - a.picks;
  });
}

export function FairPriceDashboardPage(): JSX.Element {
  const { rows, fixtures, loading, error } = useFairPriceOutputs();
  const [openFixtureId, setOpenFixtureId] = useState<string | null>(null);

  const stats = useMemo(() => deriveStats(rows, fixtures), [rows, fixtures]);
  const leagues = useMemo(() => deriveLeagueCalibration(fixtures), [fixtures]);
  const opened =
    openFixtureId !== null ? fixtures.find((f) => f.fixture_id === openFixtureId) ?? null : null;

  return (
    <div className="page" data-screen-label="06 Fair Price">
      <div className="ex-head">
        <div>
          <h1>Fair Price · intelligence</h1>
          <div className="sub">
            Independent probability model + bookmaker-consensus comparison. What STRIKZA thinks the
            market <em>should</em> be — click any fixture for the full breakdown.
          </div>
        </div>
        <div className="pill-row">
          <span className="tag phantom">
            <span className="dot" />
            Layer 1 + 2
          </span>
          <span className="tag">xG · Poisson</span>
          <span className="tag">Phase 1A</span>
        </div>
      </div>

      <div className="disclosure">
        <Icon name="info" size={14} />
        <span>
          Fair Price is the engine that powers every STRIKZA recommendation. Independent model — never
          derived from bookmaker odds. Click into a fixture to see xG inputs, scoreline distribution,
          and per-market breakdown.
        </span>
      </div>

      <div className="ex-stats">
        {stats.map((s) => (
          <div className="ex-stat" key={s.k}>
            <div className="k">{s.k}</div>
            <div className="v">
              {s.v}
              {s.unit ? <span className="unit">{s.unit}</span> : null}
            </div>
            {s.delta !== null ? <div className={`delta ${s.deltaCls}`}>{s.delta}</div> : null}
          </div>
        ))}
      </div>

      <div className="fp-dash-grid">
        <div className="fp-dash-main">
          <FairPriceList
            fixtures={fixtures}
            loading={loading}
            error={error}
            onOpen={(id) => setOpenFixtureId(id)}
          />
        </div>

        <div className="fp-dash-side">
          <div className="fp-side-card">
            <div className="fp-side-h">League coverage</div>
            <div className="fp-side-sub">Where the engine has scored fixtures in the next 7 days</div>
            {leagues.length === 0 ? (
              <div className="fp-side-sub" style={{ paddingTop: 8 }}>
                No fixtures in the window yet.
              </div>
            ) : (
              leagues.map((l) => (
                <div key={l.competition} className="fp-league-row">
                  <div className="lab">{l.competition}</div>
                  <div className="picks">{l.picks} fixt</div>
                  <div className="hit">—</div>
                  <div className="clv">{fmtSigned(l.avg_edge)}</div>
                </div>
              ))
            )}
          </div>

          <div className="fp-side-card">
            <div className="fp-side-h">How Fair Price works</div>
            <div className="fp-pipeline">
              <div className="step">
                <div className="num">1</div>
                <div className="body">
                  <div className="t">xG model</div>
                  <div className="d">
                    Per-team attack/defense profiles, home/away splits, recent form weights.
                  </div>
                </div>
              </div>
              <div className="step">
                <div className="num">2</div>
                <div className="body">
                  <div className="t">Poisson distribution</div>
                  <div className="d">
                    Score-line probabilities → P(home/draw/away), totals, BTTS, handicaps.
                  </div>
                </div>
              </div>
              <div className="step">
                <div className="num">3</div>
                <div className="body">
                  <div className="t">Fair odds</div>
                  <div className="d">Independent of bookmakers. 1 / true probability — no margin baked in.</div>
                </div>
              </div>
              <div className="step">
                <div className="num">4</div>
                <div className="body">
                  <div className="t">Consensus comparison</div>
                  <div className="d">
                    Bookmaker consensus (Pinnacle / BetExplorer / OddsPortal) gives the comparison
                    point. Edge = (fair_prob − consensus_prob) / consensus_prob.
                  </div>
                </div>
              </div>
              <div className="step">
                <div className="num">5</div>
                <div className="body">
                  <div className="t">Recommendation</div>
                  <div className="d">TAKE / WATCH / PASS based on edge threshold.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {opened !== null ? (
        <FairPriceDrawer fixture={opened} onClose={() => setOpenFixtureId(null)} />
      ) : null}
    </div>
  );
}
